import {
    ColumnDef,
    flexRender,
    getCoreRowModel,
    useReactTable,
    SortingState,
    getSortedRowModel,
    getExpandedRowModel,
    ExpandedState,
} from "@tanstack/react-table";
import { useVirtualizer } from "@tanstack/react-virtual";
import { useState, useRef, useEffect, useMemo } from "react";
import { ChevronDown, ChevronUp, ChevronsUpDown, Loader2 } from "lucide-react";
import {
    Table,
    TableHead,
    TableHeader,
    TableRow,
} from "../../component/ui/Table";

interface ConsolidatedDataTableProps<TData, TValue> {
    columns: ColumnDef<TData, TValue>[];
    data: TData[];
}

export function ConsolidatedDataTable<TData, TValue>({
    columns,
    data,
}: ConsolidatedDataTableProps<TData, TValue>) {
    const [sorting, setSorting] = useState<SortingState>([
        { id: "name", desc: false }
    ]);
    const [expanded, setExpanded] = useState<ExpandedState>({});
    const [isLoading, setIsLoading] = useState(true);
    const tableBodyRef = useRef<HTMLDivElement>(null);
    const tableRef = useRef<HTMLDivElement>(null);
    const [hoveredParentId, setHoveredParentId] = useState<string | null>(null);

    useEffect(() => {
        setIsLoading(false);
    }, [data]);

    // Use effect to automatically expand all rows when data changes
    useEffect(() => {
        if (data.length > 0) {
            const expandedState: ExpandedState = {};
            data.forEach((row: any, index: number) => {
                if (row.subRows && row.subRows.length > 0) {
                    expandedState[index] = true;
                }
            });
            setExpanded(expandedState);
        }
    }, [data]);

    // Calculate totals for MW and Projects
    const totals = useMemo(() => {
        if (!data.length) return { mw: 0, projects: 0 };
        
        return (data as any[]).reduce((acc, row) => {
            acc.mw += row.mw || 0;
            acc.projects += row.projects || 0;
            return acc;
        }, { mw: 0, projects: 0 });
    }, [data]);

    const table = useReactTable({
        data,
        columns,
        getCoreRowModel: getCoreRowModel(),
        onSortingChange: setSorting,
        getSortedRowModel: getSortedRowModel(),
        getExpandedRowModel: getExpandedRowModel(),
        onExpandedChange: setExpanded,
        state: {
            sorting,
            expanded,
        },
        enableExpanding: true,
        getSubRows: (row: any) => row.subRows,
    });

    const { rows } = table.getRowModel();

    const rowVirtualizer = useVirtualizer({
        count: rows.length,
        estimateSize: () => 28,
        getScrollElement: () => tableBodyRef.current,
        overscan: 10,
    });

    const virtualItems = rowVirtualizer.getVirtualItems();

    if (isLoading) {
        return (
            <div className="h-[450px] flex flex-col m-2 items-center justify-center">
                <div className="inset-0 flex items-center justify-center">
                    <Loader2 className="h-8 w-8 animate-spin stroke-black dark:stroke-white" />
                </div>
            </div>
        );
    }

    if (data.length === 0) {
        return (
            <div className="h-[450px] flex flex-col m-2 items-center justify-center">
                <div className="text-[#535353] dark:text-[#CEE0EF]">No data to display</div>
            </div>
        );
    }

    // Calculate fixed column widths
    const columnWidths = table.getAllColumns().map(col => ({
        id: col.id,
        width: `${100 / table.getAllColumns().length}%`
    }));

    return (
        <div className="flex flex-col min-h-0 h-full w-full">
            <div ref={tableRef} className="relative h-full min-h-0 flex flex-col my-0 mx-2">
                <div className="w-full h-full overflow-x-auto flex-1">
                    <div className="inline-block min-w-full h-full">
                        <div className="relative">
                            <div className="sticky top-0 z-10 bg-white dark:bg-[#2D3748]">
                                <Table>
                                    <TableHeader>
                                        {table.getHeaderGroups().map((headerGroup) => (
                                            <TableRow
                                                key={headerGroup.id}
                                                className="border-b border-[#C6C5C5] dark:border-b dark:border-[#7A8690]"
                                            >
                                                {headerGroup.headers.map((header, index) => (
                                                    <TableHead
                                                        key={header.id}
                                                        className="h-[36px] px-4 bg-white dark:bg-[#2D3748] whitespace-nowrap font-bold"
                                                        onClick={header.column.getToggleSortingHandler()}
                                                        style={{
                                                            width: columnWidths[index].width,
                                                            cursor: header.column.getCanSort() ? "pointer" : "default"
                                                        }}
                                                    >
                                                        <div className="flex items-center gap-2">
                                                            <div className="truncate flex-grow">
                                                                {flexRender(
                                                                    header.column.columnDef.header,
                                                                    header.getContext()
                                                                )}
                                                            </div>
                                                            {header.column.getCanSort() && (
                                                                <div className="flex-none">
                                                                    {{
                                                                        asc: <ChevronUp className="h-4 w-4" />,
                                                                        desc: <ChevronDown className="h-4 w-4" />,
                                                                    }[header.column.getIsSorted() as string] ?? (
                                                                        <ChevronsUpDown className="h-4 w-4 opacity-50" />
                                                                    )}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </TableHead>
                                                ))}
                                            </TableRow>
                                        ))}
                                    </TableHeader>
                                </Table>
                            </div>
                            <div
                                ref={tableBodyRef}
                                className="h-full flex-1 overflow-y-auto relative"
                            >
                                <div className="inline-block min-w-full">
                                    <div
                                        style={{
                                            height: `${rowVirtualizer.getTotalSize()}px`,
                                            position: "relative",
                                        }}
                                    >
                                        {virtualItems.map((virtualRow) => {
                                            const row = rows[virtualRow.index];
                                            // Parent ID for child rows will be the ID of their parent
                                            const parentId = row.depth === 0 ? row.id : row.id.split('.').slice(0, -1).join('.');
                                            
                                            return (
                                                <div
                                                    key={row.id}
                                                    data-index={virtualRow.index}
                                                    data-row-id={row.id}
                                                    data-parent-id={parentId}
                                                    data-is-parent={row.depth === 0 ? "true" : "false"}
                                                    ref={rowVirtualizer.measureElement}
                                                    className={`absolute top-0 left-0 w-full ${row.depth === 0 && virtualRow.index !== 0 ? 'border-t border-[#C6C5C5] dark:border-t dark:border-[#7A8690]' : ''} 
                                                    ${(row.depth === 0 && row.id === hoveredParentId) || (row.depth > 0 && parentId === hoveredParentId) ? 'bg-[#F9F9F9] dark:bg-[#4A5568]' : ''}
                                                    ${row.id !== hoveredParentId && parentId !== hoveredParentId ? 'hover:bg-[#F9F9F9] dark:hover:bg-[#4A5568]' : ''} 
                                                    h-[28px] flex`}
                                                    style={{
                                                        transform: `translateY(${virtualRow.start}px)`,
                                                    }}
                                                    onMouseEnter={() => {
                                                        if (row.depth === 0) {
                                                            setHoveredParentId(row.id);
                                                        }
                                                    }}
                                                    onMouseLeave={() => {
                                                        if (row.depth === 0) {
                                                            setHoveredParentId(null);
                                                        }
                                                    }}
                                                >
                                                    {row.getVisibleCells().map((cell, index) => (
                                                        <div
                                                            key={cell.id}
                                                            className="flex items-center text-sm px-4 text-[#535353] dark:text-[#CEE0EF]"
                                                            style={{
                                                                width: columnWidths[index].width,
                                                                paddingLeft: '16px'
                                                            }}
                                                        >
                                                            <div className="truncate w-full">
                                                                {flexRender(
                                                                    cell.column.columnDef.cell,
                                                                    cell.getContext()
                                                                )}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div 
                    className="w-full h-[28px] flex border-t border-[#C6C5C5] dark:border-[#7A8690] bg-white dark:bg-[#2D3748] font-bold"
                >
                    {columns.map((col, index) => {
                        const columnId = (col as any).accessorKey;
                        return (
                            <div
                                key={`total-${columnId}`}
                                className="flex items-center px-4 text-sm text-[#535353] dark:text-[#CEE0EF]"
                                style={{
                                    width: columnWidths[index].width,
                                    paddingLeft: '16px'
                                }}
                            >
                                {columnId === "name" ? (
                                    <div className="text-black dark:text-white">Total</div>
                                ) : columnId === "mw" ? (
                                    <div className="text-right w-full">{totals.mw.toFixed(2)} MW</div>
                                ) : columnId === "projects" ? (
                                    <div className="text-right w-full">{totals.projects}</div>
                                ) : (
                                    <div></div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}